import React, { useState } from "react";
import { Menu, X, Camera, Sparkles } from "lucide-react";

const Navbar = ({ scrolled, activeSection, scrollToSection }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  const sections = [
    { id: "home", label: "الرئيسية", icon: null },
    { id: "portfolio", label: "الأعمال", icon: null },
    { id: "about", label: "من أنا", icon: null },
    { id: "services", label: "الخدمات", icon: null },
    { id: "contact", label: "تواصل معنا", icon: Sparkles },
  ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-500 ${
        scrolled 
          ? "bg-white/95 backdrop-blur-xl shadow-lg border-b border-gray-200" 
          : "bg-transparent"
      }`}
    >
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div 
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => scrollToSection("home")}
          >
            <div className={`relative transition-all duration-300 ${
              scrolled ? "scale-90" : "scale-100"
            }`}>
              {/* Logo with Animation */}
              <div className="relative">
                <img
                  src="logoKemo-pica.png"
                  alt="Kemo Photography"
                  className="h-14 w-auto transition-transform duration-300 group-hover:scale-110"
                />
                {/* Glow Effect on Hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-gray-400 to-gray-600 blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300 -z-10" />
              </div>
            </div>
            
            {/* Text Logo (optional - hidden on small screens) */}
            <div className="hidden sm:block">
              <div className={`font-black text-xl transition-colors duration-300 ${
                scrolled ? "text-gray-900" : "text-white"
              }`}>
                KEMO
              </div>
              <div className={`text-xs font-medium transition-colors duration-300 ${
                scrolled ? "text-gray-600" : "text-gray-300"
              }`}>
                PHOTOGRAPHY
              </div>
            </div>
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center gap-2" dir="rtl">
            {sections.map((item) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`group relative px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? scrolled
                        ? "text-gray-900 bg-gray-100"
                        : "text-white bg-white/10 backdrop-blur-sm"
                      : scrolled
                      ? "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                      : "text-gray-300 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {/* Active Indicator */}
                  {isActive && (
                    <div className={`absolute inset-0 rounded-full border-2 transition-colors duration-300 ${
                      scrolled ? "border-gray-900" : "border-white"
                    }`} />
                  )}
                  
                  <span className="relative z-10 flex items-center gap-2">
                    {item.label}
                    {Icon && <Icon className="w-4 h-4" />}
                  </span>

                  {/* Hover Effect */}
                  <div className={`absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 ${
                    scrolled 
                      ? "bg-gradient-to-r from-gray-100 to-gray-200" 
                      : "bg-white/10 backdrop-blur-sm"
                  }`} />
                </button>
              );
            })}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`md:hidden relative p-3 rounded-xl transition-all duration-300 ${
              scrolled 
                ? "bg-gray-100 hover:bg-gray-200" 
                : "bg-white/10 backdrop-blur-sm hover:bg-white/20"
            }`}
          >
            <div className="relative w-6 h-6">
              <Menu 
                className={`absolute inset-0 transition-all duration-300 ${
                  scrolled ? "text-gray-900" : "text-white"
                } ${isMenuOpen ? "opacity-0 rotate-90 scale-0" : "opacity-100 rotate-0 scale-100"}`}
              />
              <X 
                className={`absolute inset-0 transition-all duration-300 ${
                  scrolled ? "text-gray-900" : "text-white"
                } ${isMenuOpen ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-90 scale-0"}`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 w-full transition-all duration-500 ${
          isMenuOpen 
            ? "translate-y-0 opacity-100 pointer-events-auto" 
            : "-translate-y-full opacity-0 pointer-events-none"
        }`}
      >
        <div className="bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-xl">
          <div className="px-4 py-6 space-y-2">
            {sections.map((item, index) => {
              const Icon = item.icon;
              const isActive = activeSection === item.id;
              
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    scrollToSection(item.id);
                    setIsMenuOpen(false);
                  }}
                  className={`w-full group relative overflow-hidden rounded-2xl transition-all duration-300 ${
                    isActive
                      ? "bg-gray-900 text-white shadow-lg scale-[1.02]"
                      : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                  }`}
                  style={{
                    transitionDelay: `${index * 50}ms`,
                    transform: isMenuOpen ? 'translateX(0)' : 'translateX(-100%)',
                  }}
                >
                  <div className="relative z-10 flex items-center justify-between px-6 py-4">
                    <span className="text-base font-medium flex items-center gap-2">
                      {item.label}
                      {Icon && <Icon className="w-4 h-4" />}
                    </span>
                    
                    {/* Arrow Indicator */}
                    <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                      isActive ? "bg-white scale-100" : "bg-gray-400 scale-0 group-hover:scale-100"
                    }`} />
                  </div>

                  {/* Slide Effect */}
                  <div className={`absolute inset-0 transition-transform duration-300 -z-10 ${
                    isActive 
                      ? "translate-x-0 bg-gradient-to-r from-gray-900 to-gray-800" 
                      : "translate-x-[-100%] bg-gradient-to-r from-gray-100 to-gray-50 group-hover:translate-x-0"
                  }`} />
                </button>
              );
            })}
          </div>

          {/* Mobile Menu Footer */}
          <div className="px-4 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
              <Camera className="w-4 h-4" />
              <span className="font-medium">KEMO PHOTOGRAPHY</span>
            </div>
          </div>
        </div>
      </div>

      {/* Backdrop Overlay for Mobile Menu */}
      {isMenuOpen && (
        <div 
          className="md:hidden fixed inset-0 bg-black/20 backdrop-blur-sm -z-10"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </nav>
  );
};

export default Navbar;